/**
 * Team confirmation chip shared by the staff auth pages.
 *
 * Shown once a team code has resolved (`GET /signup/v1/team-lookup`): the
 * program name, the code itself and — where the page allows it — a way back to
 * the team step. Also home to the lookup-failure copy so login and
 * forgot-password phrase an unknown code identically.
 */
import { ApiError } from '../../lib/api-error';
import type { TeamLookup } from '../../lib/api-types';

/** Copy for a failed team lookup, keyed off the API failure. */
export function toTeamLookupMessage(error: unknown, slug: string): string {
  if (!ApiError.isApiError(error)) return 'Could not look up that team code.';
  if (error.status === 404) {
    return `No team portal found for “${slug}”. Check the code with your Team Admin.`;
  }
  return error.message;
}

export function TeamChip({
  team,
  onSwitch,
}: {
  team: Pick<TeamLookup, 'name' | 'slug'>;
  onSwitch?: () => void;
}): JSX.Element {
  return (
    <div className="auth__team-chip" data-testid="team-chip">
      <img src="/images/logo-mark.png" alt="" width={24} height={24} />
      <span className="auth__team-chip__name">{team.name}</span>
      <span className="auth__team-chip__code mono">
        <span className="visually-hidden">Code </span>
        {team.slug}
      </span>
      {onSwitch ? (
        <button type="button" className="auth__link-button" onClick={onSwitch}>
          Switch team
        </button>
      ) : null}
    </div>
  );
}
